import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";

import ReportesApi from "api/reportesApi";
import TablaReporte from "./TablaReporte";
import ModalGrafico from "./ModalGrafico";
import { exportarAExcel, prepararDatosParaExportacion } from "../utils/exportarExcel";
import { exportarAPDF, formatters } from "../utils/exportarPDF";

/**
 * Componente de reporte de tiempos de gestión
 * Muestra días promedio de gestión por estado
 */
function ReporteTiemposGestion({ filtros = {} }) {
  const [datos, setDatos] = useState([]);
  const [promedioGeneral, setPromedioGeneral] = useState(0);
  const [totalProcesos, setTotalProcesos] = useState(0);
  const [datosGrafico, setDatosGrafico] = useState({});
  const [loading, setLoading] = useState(true);
  const [modalGraficoAbierto, setModalGraficoAbierto] = useState(false);

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ReportesApi.obtenerReporteTiemposGestion(filtros);
      setDatos(response.data.datos || []);
      setPromedioGeneral(response.data.promedio_general || 0);
      setTotalProcesos(response.data.total_procesos_analizados || 0);
      setDatosGrafico(response.data.grafico || {});
    } catch (error) {
      console.error("Error al cargar reporte de tiempos:", error);
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const columnas = [
    {
      header: "Estado",
      field: "estado_nombre",
      align: "left",
      width: "50%",
    },
    {
      header: "Cantidad de Procesos",
      field: "cantidad_procesos",
      align: "center",
      width: "25%",
      format: (valor) => formatters.numero(valor),
    },
    {
      header: "Días Promedio de Gestión",
      field: "promedio_dias",
      align: "center",
      width: "25%",
      render: (fila) => {
        const valor = fila.promedio_dias;
        let color = "info";

        // Colorear según tiempo de gestión
        if (valor > 60) color = "error";
        else if (valor > 30) color = "warning";
        else color = "success";

        return (
          <Chip label={`${valor} días`} color={color} size="small" sx={{ fontWeight: "bold" }} />
        );
      },
    },
  ];

  const handleExportarExcel = () => {
    const datosFormateados = prepararDatosParaExportacion(
      datos.map((item) => ({
        Estado: item.estado_nombre,
        "Cantidad de Procesos": item.cantidad_procesos,
        "Días Promedio": item.promedio_dias,
      })),
      {
        "Cantidad de Procesos": "numero",
      }
    );

    exportarAExcel(datosFormateados, "Reporte_Tiempos_Gestion", "Tiempos", {
      titulo: "Reporte de Tiempos de Gestión",
      filtros: filtros,
    });
  };

  const handleExportarPDF = () => {
    exportarAPDF({
      titulo: "Reporte de Tiempos de Gestión",
      columnas: columnas.map((col) => ({
        header: col.header,
        field: col.field,
        format: col.field === "promedio_dias" ? (v) => `${v} días` : formatters.numero,
      })),
      datos: datos,
      filtros: filtros,
      totales: {
        "Total de Procesos Analizados": formatters.numero(totalProcesos),
        "Promedio General de Días": `${promedioGeneral} días`,
      },
    });
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={4}>
        <Card
          sx={{
            background: "linear-gradient(195deg, #49a3f1 0%, #1A73E8 100%)",
          }}
        >
          <SuiBox p={2} textAlign="center">
            <SuiTypography variant="h3" fontWeight="bold" sx={{ color: "white !important" }}>
              {formatters.numero(totalProcesos)}
            </SuiTypography>
            <SuiTypography
              variant="button"
              sx={{ color: "white !important", opacity: 0.9 }}
              fontWeight="medium"
            >
              Procesos Analizados
            </SuiTypography>
          </SuiBox>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <Card
          sx={{
            background: "linear-gradient(195deg, #FFA726 0%, #FB8C00 100%)",
          }}
        >
          <SuiBox p={2} textAlign="center">
            <SuiTypography variant="h3" fontWeight="bold" sx={{ color: "white !important" }}>
              {promedioGeneral}
            </SuiTypography>
            <SuiTypography
              variant="button"
              sx={{ color: "white !important", opacity: 0.9 }}
              fontWeight="medium"
            >
              Días Promedio General
            </SuiTypography>
          </SuiBox>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <TablaReporte
          titulo="Tiempos de Gestión por Estado"
          columnas={columnas}
          datos={datos}
          loading={loading}
          onVerGrafico={() => setModalGraficoAbierto(true)}
          onExportarExcel={handleExportarExcel}
          onExportarPDF={handleExportarPDF}
          paginacion={false}
        />
      </Grid>

      <ModalGrafico
        abierto={modalGraficoAbierto}
        onCerrar={() => setModalGraficoAbierto(false)}
        titulo="Gráfico de Tiempos de Gestión"
        tipoGrafico="bar"
        datosGrafico={datosGrafico}
      />
    </Grid>
  );
}

ReporteTiemposGestion.propTypes = {
  filtros: PropTypes.object,
};

export default ReporteTiemposGestion;
